import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import useMatchMedia from 'hooks/useMatchMedia';
import useAddEventListener from 'hooks/useAddEventListener';
import { Button } from 'ncoded-component-library';
import Portal from 'components/Portal';
import { Position } from 'models/Position';
import { TRANSITION_TIME } from 'data/global';
import { ObjectValues, XOR } from 'types';
import XIcon from 'icons/X.icon';
import useFloatingBannerPositioning from './hooks/useFloatingBannerPositioning';

import './FloatingBanner.styles.scss';

export const FLOATING_BANBER_COLORS = {
  GREEN: 'green',
  BLACK: 'black',
  WHITE: 'white',
};

type FlaotingBannerColors = ObjectValues<typeof FLOATING_BANBER_COLORS>;

export type FloatingBannerProps = {
  className?: string;
  color?: FlaotingBannerColors;
  preferredPosition?: Position;
  showOnPhablet?: boolean;
  hasCloseButton?: boolean;
  hasFadeEffect?: boolean;
  onClose?: () => void;
} & XOR<{ anchor: ReactNode }, { selector: string }> &
  XOR<{ onHover: boolean }, { showBannerNotification?: boolean }>;

const FloatingBanner: React.FC<FloatingBannerProps> = (props) => {
  const {
    className,
    children,
    anchor,
    selector,
    showBannerNotification,
    onHover,
    showOnPhablet,
    hasFadeEffect = false,
    hasCloseButton = true,
    color = FLOATING_BANBER_COLORS.GREEN,
    preferredPosition,
    onClose,
  } = props;

  const isPhablet = useMatchMedia('isPhablet');
  const isTouchDevice = useMatchMedia('isTouchDevice');

  const showBanner = showBannerNotification && (showOnPhablet || !isPhablet);

  const [isInViewport, setIsInViewport] = useState(onHover || showBanner);
  const [isVisible, setIsVisible] = useState(showBanner);
  const [isMounted, setIsMounted] = useState(showBanner);

  const {
    position,
    isPositioned,
    bannerPosition,
    anchorRef,
    contentRef,
    updateBannerPosition,
    resetIsPositioned,
  } = useFloatingBannerPositioning({
    onHover,
    preferredPosition,
    setIsInViewport,
  });

  const isShown = isInViewport && isVisible;

  // without a fade there is nothing to wait for, so the portal tracks `isShown`
  // directly and banners that opted out keep their original timing
  const isOpen = hasFadeEffect ? isMounted : isShown;

  const classes = classNames(
    'floating-banner',
    color,
    bannerPosition,
    {
      'floating-banner--fade': hasFadeEffect,
      'floating-banner--visible': isShown && isPositioned,
    },
    className,
  );

  const onMouseEnter = useCallback(() => {
    if (isTouchDevice) return;

    setIsVisible(true);
  }, [isTouchDevice]);

  const onMouseLeave = useCallback(() => {
    if (isTouchDevice) return;

    setIsVisible(false);
  }, [isTouchDevice]);

  // touch has no hover: a tap must toggle the banner directly, otherwise the
  // browser eats the first tap as a synthetic hover
  const toggleOnTap = useCallback(() => {
    if (!onHover || !isTouchDevice) return;

    setIsVisible((prev) => !prev);
  }, [onHover, isTouchDevice]);

  const closeOnOutsideTap = useCallback(
    (event: PointerEvent) => {
      const target = event.target as Node;

      if (
        anchorRef.current?.contains(target) ||
        contentRef.current?.contains(target)
      )
        return;

      setIsVisible(false);
    },
    [anchorRef, contentRef],
  );

  const setAnchorRef = useCallback(() => {
    if (!selector) return;

    const el = document.querySelector(selector) as HTMLDivElement;

    if (!el) return;

    anchorRef.current = el;

    if (!onHover) return;

    el.classList.add('clickable');
    el.addEventListener('mouseenter', onMouseEnter);
    el.addEventListener('mouseleave', onMouseLeave);
    el.addEventListener('click', toggleOnTap);

    return () => {
      el.removeEventListener('mouseenter', onMouseEnter);
      el.removeEventListener('mouseleave', onMouseLeave);
      el.removeEventListener('click', toggleOnTap);
    };
  }, [anchorRef, selector, onHover, onMouseEnter, onMouseLeave, toggleOnTap]);

  useEffect(() => {
    const cleanup = setAnchorRef();

    return () => {
      cleanup?.();
    };
  }, [setAnchorRef]);

  useEffect(() => {
    if (isVisible) {
      updateBannerPosition();

      // The phone keyboard resizes the visual viewport rather than the window,
      // so a banner holding a field only learns it has been covered from here.
      const { visualViewport } = window;

      window.addEventListener('scroll', updateBannerPosition, true);
      window.addEventListener('resize', updateBannerPosition, true);
      visualViewport?.addEventListener('scroll', updateBannerPosition);
      visualViewport?.addEventListener('resize', updateBannerPosition);

      return () => {
        window.removeEventListener('scroll', updateBannerPosition, true);
        window.removeEventListener('resize', updateBannerPosition, true);
        visualViewport?.removeEventListener('scroll', updateBannerPosition);
        visualViewport?.removeEventListener('resize', updateBannerPosition);
      };
    }
  }, [isVisible, anchor, selector, setAnchorRef, updateBannerPosition]);

  useEffect(() => {
    if (onHover) return;

    setIsVisible(showBannerNotification);
  }, [onHover, showBannerNotification]);

  useAddEventListener({
    event: 'pointerdown',
    handler: closeOnOutsideTap,
    capture: true,
    enabled: !!onHover && isTouchDevice && isVisible,
  });

  // outlive `isShown` so the banner can fade out before it leaves the portal
  useEffect(() => {
    if (!hasFadeEffect) return;

    if (isShown) {
      setIsMounted(true);
      return;
    }

    // the next show has to wait for a fresh position, otherwise the banner
    // fades in at the anchor it was measured against last time
    resetIsPositioned();

    const timeout = setTimeout(() => setIsMounted(false), TRANSITION_TIME);

    return () => clearTimeout(timeout);
  }, [isShown, hasFadeEffect, resetIsPositioned]);

  return (
    <>
      {anchor && (
        <div
          className={classNames('floating-banner__anchor', {
            clickable: onHover,
          })}
          ref={anchorRef}
          {...(onHover && {
            onMouseEnter,
            onMouseLeave,
            onClick: toggleOnTap,
          })}
        >
          {anchor}
        </div>
      )}

      <Portal isOpen={isOpen}>
        <div className={classes} style={position} ref={contentRef}>
          {children}

          {hasCloseButton && (
            <Button
              variant="icon"
              className="floating-banner__close"
              icon={XIcon}
              onClick={(e) => {
                e.stopPropagation();
                setIsInViewport(false);
                onClose?.();
              }}
            />
          )}
        </div>
      </Portal>
    </>
  );
};

export default FloatingBanner;
